import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FiArrowRight, FiSearch, FiX, FiShuffle, FiCalendar, FiType, FiClock, FiBookOpen } from 'react-icons/fi';
import { FaTwitter, FaLinkedin } from 'react-icons/fa';
import axios from 'axios';
import SnakeGame from './SnakeGame';
import './RetroPage.css';

interface Project {
  id: string;
  title: string;
  year: number;
  description: string;
  image: string;
  techStack: string[];
  features: string[];
  url: string | null;
  isGame?: boolean;
}

type SortOption = 'random' | 'name' | 'date';

const HomePage: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [filteredProjects, setFilteredProjects] = useState<Project[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('date');
  const [loading, setLoading] = useState(true);
  const [activeGame, setActiveGame] = useState<string | null>(null);
  const navigate = useNavigate();

  // Client logos data with local images
  const clientLogos = [
    { name: 'Honda', logoFile: 'honda-logo.png' },
    { name: 'Discovery', logoFile: 'discovery-logo.png' },
    { name: 'Nike', logoFile: 'nike-logo.png' },
    { name: 'Disney', logoFile: 'disney-logo.png' },
    { name: 'American Airlines', logoFile: 'american-airlines-logo.png' },
    { name: 'Microsoft', logoFile: 'microsoft-logo.png' },
    { name: 'Toyota', logoFile: 'toyota-logo.png' },
    { name: 'Nickelodeon', logoFile: null },
    { name: 'Hyundai', logoFile: null },
    { name: 'Ford', logoFile: 'ford-logo.png' },
    { name: 'Sapporo', logoFile: null },
    { name: 'MTV', logoFile: null }
  ];

  // Function to shuffle array randomly
  const shuffleArray = <T,>(array: T[]): T[] => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Function to sort projects based on selected option
  const sortProjects = (projectsToSort: Project[], sortOption: SortOption): Project[] => {
    switch (sortOption) {
      case 'name':
        return [...projectsToSort].sort((a, b) => a.title.localeCompare(b.title));
      case 'date':
        return [...projectsToSort].sort((a, b) => {
          // First sort by year (most recent first)
          const yearDiff = b.year - a.year;
          // If same year, then sort alphabetically by title
          if (yearDiff === 0) {
            return a.title.localeCompare(b.title);
          }
          return yearDiff;
        });
      case 'random':
        return shuffleArray(projectsToSort);
      default:
        return projectsToSort;
    }
  };

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await axios.get<Project[]>('/api/projects');
        const sortedProjects = sortProjects(response.data, 'date');
        setProjects(sortedProjects);
        setFilteredProjects(sortedProjects);
      } catch (error) {
        console.error('Error fetching projects:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  // Apply sorting when sort option changes
  useEffect(() => {
    if (projects.length > 0) {
      const sortedProjects = sortProjects(projects, sortBy);
      setProjects(sortedProjects);
      
      // Also apply sorting to filtered projects if there's a search term
      if (searchTerm) {
        const filteredSorted = sortProjects(filteredProjects, sortBy);
        setFilteredProjects(filteredSorted);
      } else {
        setFilteredProjects(sortedProjects);
      }
    }
  }, [sortBy]);

  // Filter projects based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredProjects(projects);
      return;
    }

    const filtered = projects.filter(project => {
      const searchLower = searchTerm.toLowerCase();
      
      // Search in title
      if (project.title.toLowerCase().includes(searchLower)) return true;
      
      // Search in description
      if (project.description.toLowerCase().includes(searchLower)) return true;
      
      // Search in tech stack
      if (project.techStack.some(tech => 
        tech.toLowerCase().includes(searchLower)
      )) return true;
      
      // Search in features
      if (project.features.some(feature => 
        feature.toLowerCase().includes(searchLower)
      )) return true;
      
      return false;
    });

    // Apply current sorting to filtered results
    const sortedFiltered = sortProjects(filtered, sortBy);
    setFilteredProjects(sortedFiltered);
  }, [searchTerm, projects, sortBy]);

  const handleProjectClick = (project: Project) => {
    if (project.isGame) {
      setActiveGame(activeGame === project.id ? null : project.id);
    } else {
      navigate(`/project/${project.id}`);
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100
      }
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        Loading projects...
      </div>
    );
  }

  return (
    <div className="homepage">
      <button
        className="retro-mode-btn"
        onClick={() => navigate('/retro')}
        title="Switch to Retro Mode (1998 Edition)"
        aria-label="Switch to the 1998 retro portfolio"
      >
        <span className="retro-mode-btn-icon">N</span>
        Retro Mode
      </button>
      <motion.div
        className="hero-section"
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <div className="hero-intro" style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          marginBottom: '2rem'
        }}>
          <motion.div
            className="portrait-frame"
            style={{
              width: '120px',
              height: '120px',
              borderRadius: '50%',
              overflow: 'hidden',
              border: '4px solid rgba(255, 255, 255, 0.3)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
              backdropFilter: 'blur(10px)',
              background: 'rgba(255, 255, 255, 0.1)',
              marginBottom: '1.5rem'
            }}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            whileHover={{ scale: 1.05, boxShadow: '0 12px 40px rgba(0, 0, 0, 0.4)' }}
          >
            <img 
              src="/images/myportrait-320.jpg"
              alt="Portrait of Glen Rhodes"
              width={120}
              height={120}
              loading="eager"
              decoding="async"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
          </motion.div>
          
          <h1 className="hero-title">Glen Rhodes</h1>
          <p className="hero-subtitle">
            Full-Stack Developer, AI Engineer & Machine Learning Specialist crafting innovative solutions for the digital world.
            Explore my portfolio of cutting-edge projects from {new Date().getFullYear()}.
          </p>
        </div>
        
        <motion.div
          className="social-links"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.6 }}
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '1rem',
            marginTop: '2rem',
            marginBottom: '2rem',
            padding: '0 1rem'
          }}
        >
          <motion.a
            href="https://twitter.com/glenrhodes"
            target="_blank"
            rel="noopener noreferrer"
            className="social-link"
            aria-label="Follow Glen Rhodes on X, formerly Twitter"
            whileHover={{ scale: 1.1, y: -2 }}
            whileTap={{ scale: 0.95 }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1.25rem',
              background: 'rgba(255, 255, 255, 0.15)',
              border: '2px solid rgba(29, 161, 242, 0.8)',
              borderRadius: '25px',
              color: '#ffffff',
              textDecoration: 'none',
              fontSize: '0.9rem',
              fontWeight: '600',
              transition: 'all 0.3s ease',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)'
            }}
          >
            <FaTwitter size={18} style={{ color: '#1DA1F2' }} />
            <span>@glenrhodes</span>
          </motion.a>

          <motion.a
            href="https://www.linkedin.com/in/rhodesglen/"
            target="_blank"
            rel="noopener noreferrer"
            className="social-link"
            aria-label="View Glen Rhodes on LinkedIn"
            whileHover={{ scale: 1.1, y: -2 }}
            whileTap={{ scale: 0.95 }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1.25rem',
              background: 'rgba(255, 255, 255, 0.15)',
              border: '2px solid rgba(0, 119, 181, 0.8)',
              borderRadius: '25px',
              color: '#ffffff',
              textDecoration: 'none',
              fontSize: '0.9rem',
              fontWeight: '600',
              transition: 'all 0.3s ease',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)'
            }}
          >
            <FaLinkedin size={18} style={{ color: '#0077B5' }} />
            <span>LinkedIn</span>
          </motion.a>

          <motion.button
            onClick={() => navigate('/timeline')}
            className="social-link"
            aria-label="View Glen Rhodes's career timeline"
            whileHover={{ scale: 1.1, y: -2 }}
            whileTap={{ scale: 0.95 }}
            style={{
              display: 'none',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1.25rem',
              background: 'rgba(255, 255, 255, 0.15)',
              border: '2px solid rgba(255, 215, 0, 0.8)',
              borderRadius: '25px',
              color: '#ffffff',
              fontSize: '0.9rem',
              fontWeight: '600',
              transition: 'all 0.3s ease',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
              cursor: 'pointer'
            }}
          >
            <FiClock size={18} style={{ color: '#FFD700' }} />
            <span>Timeline</span>
          </motion.button>

          <motion.button
            onClick={() => navigate('/story')}
            className="social-link"
            whileHover={{ scale: 1.1, y: -2 }}
            whileTap={{ scale: 0.95 }}
            aria-label="Read Glen's story"
            style={{
              display: 'none',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1.25rem',
              background: 'rgba(255, 255, 255, 0.15)',
              border: '2px solid rgba(0, 212, 255, 0.8)',
              borderRadius: '25px',
              color: '#ffffff',
              fontSize: '0.9rem',
              fontWeight: '600',
              transition: 'all 0.3s ease',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
              cursor: 'pointer'
            }}
          >
            <FiBookOpen size={18} style={{ color: '#00D4FF' }} />
            <span>My Story</span>
          </motion.button>
        </motion.div>

        {/* Search Field */}
        <motion.div
          className="search-container"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.6 }}
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: '3rem'
          }}
        >
          <div style={{ position: 'relative', width: '100%', maxWidth: '500px' }}>
            <motion.input
              type="text"
              placeholder="Search projects by tech stack, features, or keywords..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              aria-label="Search portfolio projects"
              style={{
                width: '100%',
                padding: '1rem 3rem 1rem 3rem',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '30px',
                color: '#ffffff',
                fontSize: '1rem',
                outline: 'none',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
                transition: 'all 0.3s ease'
              }}
              whileFocus={{
                scale: 1.02,
                boxShadow: '0 6px 25px rgba(0, 0, 0, 0.3)',
                borderColor: 'rgba(255, 255, 255, 0.5)'
              }}
            />
            <FiSearch 
              size={20} 
              style={{
                position: 'absolute',
                left: '1rem',
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'rgba(255, 255, 255, 0.6)',
                pointerEvents: 'none'
              }}
            />
            {searchTerm && (
              <motion.button
                onClick={clearSearch}
                className="search-clear"
                aria-label="Clear project search"
                style={{
                  position: 'absolute',
                  right: '1rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'transparent',
                  border: 'none',
                  color: 'rgba(255, 255, 255, 0.6)',
                  cursor: 'pointer',
                  padding: '0.25rem',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                whileHover={{ 
                  color: '#ffffff',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }}
                whileTap={{ scale: 0.9 }}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
              >
                <FiX size={18} />
              </motion.button>
            )}
          </div>
        </motion.div>

        {/* Search Results Info */}
        {searchTerm && (
          <motion.div
            style={{
              textAlign: 'center',
              marginBottom: '2rem',
              color: 'rgba(255, 255, 255, 0.8)',
              fontSize: '0.9rem'
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            {filteredProjects.length > 0 ? (
              <p>Found {filteredProjects.length} project{filteredProjects.length === 1 ? '' : 's'} matching "{searchTerm}"</p>
            ) : (
              <p>No projects found matching "{searchTerm}". Try searching for technologies like "React", "Node.js", or "AI".</p>
            )}
          </motion.div>
        )}

        {/* Sort Controls */}
        <motion.div
          className="sort-controls"
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '1rem',
            marginBottom: '3rem',
            flexWrap: 'wrap'
          }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.6 }}
        >
          <span style={{ 
            color: 'rgba(255, 255, 255, 0.8)', 
            fontSize: '0.9rem',
            fontWeight: '500'
          }}>
            Sort by:
          </span>
          
          <motion.button
            onClick={() => setSortBy('random')}
            className="sort-button"
            aria-label="Sort projects randomly"
            aria-pressed={sortBy === 'random'}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.6rem 1rem',
              background: sortBy === 'random' 
                ? 'rgba(255, 255, 255, 0.25)' 
                : 'rgba(255, 255, 255, 0.1)',
              border: sortBy === 'random' 
                ? '2px solid rgba(255, 255, 255, 0.5)' 
                : '2px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '20px',
              color: '#ffffff',
              fontSize: '0.8rem',
              fontWeight: '500',
              cursor: 'pointer',
              backdropFilter: 'blur(10px)',
              transition: 'all 0.3s ease'
            }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <FiShuffle size={16} />
            Random
          </motion.button>

          <motion.button
            onClick={() => setSortBy('name')}
            className="sort-button"
            aria-label="Sort projects by name"
            aria-pressed={sortBy === 'name'}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.6rem 1rem',
              background: sortBy === 'name' 
                ? 'rgba(255, 255, 255, 0.25)' 
                : 'rgba(255, 255, 255, 0.1)',
              border: sortBy === 'name' 
                ? '2px solid rgba(255, 255, 255, 0.5)' 
                : '2px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '20px',
              color: '#ffffff',
              fontSize: '0.8rem',
              fontWeight: '500',
              cursor: 'pointer',
              backdropFilter: 'blur(10px)',
              transition: 'all 0.3s ease'
            }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <FiType size={16} />
            Name
          </motion.button>

          <motion.button
            onClick={() => setSortBy('date')}
            className="sort-button"
            aria-label="Sort projects by date"
            aria-pressed={sortBy === 'date'}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.6rem 1rem',
              background: sortBy === 'date' 
                ? 'rgba(255, 255, 255, 0.25)' 
                : 'rgba(255, 255, 255, 0.1)',
              border: sortBy === 'date' 
                ? '2px solid rgba(255, 255, 255, 0.5)' 
                : '2px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '20px',
              color: '#ffffff',
              fontSize: '0.8rem',
              fontWeight: '500',
              cursor: 'pointer',
              backdropFilter: 'blur(10px)',
              transition: 'all 0.3s ease'
            }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <FiCalendar size={16} />
            Date
          </motion.button>
        </motion.div>
      </motion.div>

      {/* Client Logos Section */}
      <motion.div
        className="client-logos-section"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8, duration: 0.8 }}
        style={{
          padding: '1rem 0 3rem',
          overflow: 'hidden',
          position: 'relative'
        }}
      >
        <div
          className="logos-scroll-container"
          style={{
            position: 'relative',
            height: '140px',
            overflow: 'hidden',
            mask: 'linear-gradient(90deg, transparent, white 10%, white 90%, transparent)',
            WebkitMask: 'linear-gradient(90deg, transparent, white 10%, white 90%, transparent)'
          }}
        >
          <motion.div
            className="logos-scroll-track"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '2rem',
              position: 'absolute',
              height: '100%',
              whiteSpace: 'nowrap'
            }}
          >
            {/* First set of logos */}
            {clientLogos.map((client, index) => (
              <motion.div
                key={`first-${index}`}
                className="logo-item"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '200px',
                  height: '120px',
                  padding: '1rem',
                  flexShrink: 0
                }}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 1.4 + index * 0.1, duration: 0.5 }}
              >
                {client.logoFile ? (
                  <img
                    src={`/images/${client.logoFile}`}
                    alt={`${client.name} logo`}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                      opacity: 0.8,
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.opacity = '1';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.opacity = '0.8';
                    }}
                    onError={(e) => {
                      // Fallback to text if image fails to load
                      e.currentTarget.style.display = 'none';
                      const textElement = e.currentTarget.nextElementSibling as HTMLElement;
                      if (textElement) {
                        textElement.style.display = 'block';
                      }
                    }}
                  />
                ) : null}
                <span
                  style={{
                    display: client.logoFile ? 'none' : 'block',
                    fontSize: '1.2rem',
                    fontWeight: '600',
                    color: 'rgba(255, 255, 255, 0.7)',
                    textAlign: 'center'
                  }}
                >
                  {client.name}
                </span>
              </motion.div>
            ))}
            
            {/* Duplicate set for seamless scrolling */}
            {clientLogos.map((client, index) => (
              <motion.div
                key={`second-${index}`}
                className="logo-item"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '200px',
                  height: '120px',
                  padding: '1rem',
                  flexShrink: 0
                }}
              >
                {client.logoFile ? (
                  <img
                    src={`/images/${client.logoFile}`}
                    alt={`${client.name} logo`}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                      opacity: 0.8,
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.opacity = '1';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.opacity = '0.8';
                    }}
                    onError={(e) => {
                      // Fallback to text if image fails to load
                      e.currentTarget.style.display = 'none';
                      const textElement = e.currentTarget.nextElementSibling as HTMLElement;
                      if (textElement) {
                        textElement.style.display = 'block';
                      }
                    }}
                  />
                ) : null}
                <span
                  style={{
                    display: client.logoFile ? 'none' : 'block',
                    fontSize: '1.2rem',
                    fontWeight: '600',
                    color: 'rgba(255, 255, 255, 0.7)',
                    textAlign: 'center'
                  }}
                >
                  {client.name}
                </span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        className="projects-grid"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        key={filteredProjects.length} // Re-animate when filter changes
      >
        {filteredProjects.map((project) => (
          <motion.div
            key={project.id}
            className="project-card"
            variants={itemVariants}
            whileHover={{ scale: 1.02 }}
            onClick={() => handleProjectClick(project)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleProjectClick(project);
              }
            }}
            role="button"
            tabIndex={0}
            aria-label={`${project.isGame ? 'Play' : 'View'} ${project.title}`}
            layout // Smooth layout animations when items are filtered
          >
            {project.isGame && activeGame === project.id ? (
              <div 
                style={{ width: '100%', height: '100%', minHeight: '300px', position: 'relative' }}
                onClick={(e) => e.stopPropagation()}
              >
                <SnakeGame cardWidth={320} cardHeight={280} />
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveGame(null);
                  }}
                  aria-label={`Close ${project.title} game`}
                  style={{
                    position: 'absolute',
                    top: '5px',
                    right: '5px',
                    background: 'rgba(0, 0, 0, 0.7)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '50%',
                    width: '24px',
                    height: '24px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    zIndex: 10
                  }}
                  title="Close game"
                >
                  ×
                </button>
              </div>
            ) : (
              <>
                <img 
                  src={project.image} 
                  alt={project.title}
                  className="project-image"
                  loading="lazy"
                  decoding="async"
                />
                <div className="project-content">
                  <h3 className="project-title">{project.title}</h3>
                  <p className="project-description">{project.description}</p>
                  
                  <div className="tech-stack">
                    {project.techStack.slice(0, 4).map((tech, index) => (
                      <span key={index} className="tech-badge">
                        {tech}
                      </span>
                    ))}
                    {project.techStack.length > 4 && (
                      <span className="tech-badge">+{project.techStack.length - 4} more</span>
                    )}
                  </div>
                  
                  <div className="project-link">
                    <span>{project.isGame ? 'Click to Play' : 'View Details'}</span>
                    <FiArrowRight />
                  </div>
                </div>
              </>
            )}
          </motion.div>
        ))}
      </motion.div>

      {/* No results message */}
      {searchTerm && filteredProjects.length === 0 && (
        <motion.div
          style={{
            textAlign: 'center',
            padding: '4rem 2rem',
            color: 'rgba(255, 255, 255, 0.6)'
          }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <h3 style={{ 
            fontSize: '1.5rem', 
            marginBottom: '1rem',
            color: 'rgba(255, 255, 255, 0.8)'
          }}>
            No projects found
          </h3>
          <p style={{ marginBottom: '1rem' }}>
            Try searching for popular technologies like:
          </p>
          <div style={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: '0.5rem', 
            justifyContent: 'center' 
          }}>
            {['React', 'Node.js', 'Python', 'AI', 'Flask', 'GPT-4', 'PostgreSQL', 'OpenAI'].map(tech => (
              <motion.button
                key={tech}
                onClick={() => setSearchTerm(tech)}
                aria-label={`Search projects for ${tech}`}
                style={{
                  padding: '0.5rem 1rem',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '20px',
                  color: '#ffffff',
                  cursor: 'pointer',
                  fontSize: '0.8rem'
                }}
                whileHover={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  scale: 1.05
                }}
                whileTap={{ scale: 0.95 }}
              >
                {tech}
              </motion.button>
            ))}
          </div>
        </motion.div>
      )}

      <motion.footer
        className="site-footer"
        style={{
          textAlign: 'center',
          padding: '4rem 0 2rem',
          color: 'rgba(255, 255, 255, 0.7)',
          fontSize: '0.9rem'
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1, duration: 0.5 }}
      >
        <p>© {new Date().getFullYear()} Glen Rhodes. All rights reserved.</p>
      </motion.footer>
    </div>
  );
};

export default HomePage; 